import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyTableRow from '../../Components/GatewayContagem/EmptyTableRow';
import { TABLE_HEAD_ROW_SX, fmtDateTime } from '../../Utils/format';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import usePermissions from '../../Context/Hooks/usePermissions';
import { PERMISSIONS } from '../../Constants/permissions';
import {
  Box, Breadcrumbs, Button, Card, CardContent, IconButton,
  LinearProgress, Link, Skeleton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import StorefrontIcon from '@mui/icons-material/Storefront';
import toast from 'react-hot-toast';
import { deviceAPI } from '../../API/deviceAPI';
import { storeAPI } from '../../API/storeAPI';
import { clientAPI } from '../../API/clientAPI';
import DeviceStatusIndicator from '../../Components/GatewayContagem/DeviceStatusIndicator';
import StoreStatsSection from '../../Components/GatewayContagem/StoreStatsSection';
import AccessDeniedState from '../../Components/GatewayContagem/AccessDeniedState';
import NotFoundState from '../../Components/GatewayContagem/NotFoundState';
import ConfirmModal from '../../Components/Modal/ConfirmModal';
import { isForbiddenError, isNotFoundError } from '../../Utils/apiHelpers';

import LoadingSkeleton from '../../Components/GatewayContagem/LoadingSkeleton';

// ── Info row helper ────────────────────────────────────────────────────────────
function InfoRow({ label, value, loading = false }) {
  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="baseline"
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 140 }}
      >{label}</Typography>
      <LoadingSkeleton loading={loading} width={140} height={20}>
        <Typography
          variant="body2"
          fontWeight={500}
        >{value ?? '—'}</Typography>
      </LoadingSkeleton>
    </Stack>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
const StoreDetailPage = () => {
  const { t } = useTranslation('clients');
  const { storeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const canAccessClients = can(PERMISSIONS.CLIENTS_VIEW);

  const backPath = location.state?.from === 'countings'
    ? '/Gateway/Countings'
    : (canAccessClients ? '/Gateway/Clients' : '/Gateway/Dashboard');
  const breadcrumbLabel = location.state?.from === 'countings'
    ? t('storeDetail.breadcrumbContagens')
    : (canAccessClients ? t('storeDetail.breadcrumb') : 'Dashboard');

  const [store, setStore] = useState(null);
  const [client, setClient] = useState(null);
  const [storeLoading, setStoreLoading] = useState(true);
  const [isForbidden, setIsForbidden] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (!storeId) return;
    setStoreLoading(true);
    setIsForbidden(false);
    setIsNotFound(false);
    storeAPI.getById(storeId)
      .then(res => {
        if (isForbiddenError(res)) {
          setIsForbidden(true);
          return null;
        }
        if (isNotFoundError(res)) {
          setIsNotFound(true);
          return null;
        }
        if (res && !res.error && res.value) {
          setStore(res.value);
          return clientAPI.getById(res.value.clientId);
        }
        return null;
      })
      .then(cr => {
        if (cr && !cr.error && cr.value) setClient(cr.value);
      })
      .catch(err => {
        if (isForbiddenError(null, err)) setIsForbidden(true);
        else if (isNotFoundError(null, err)) setIsNotFound(true);
      })
      .finally(() => setStoreLoading(false));
  }, [storeId]);

  const [historicalDevices, setHistoricalDevices] = useState([]);

  const loadDevices = useCallback(async () => {
    if (!storeId) {
      return;
    }
    setLoading(true);
    try {
      const [res, histRes] = await Promise.all([
        deviceAPI.getByStore(storeId),
        deviceAPI.getStoreHistory(storeId),
      ]);
      setDevices(res && !res.error && Array.isArray(res.value) && res.value.length ? res.value : []);

      const histList = Array.isArray(histRes?.value) ? histRes.value : [];
      if (histList.length > 0) {
        const deviceMap = new Map();
        histList.forEach(item => {
          if (!deviceMap.has(item.deviceId)) {
            deviceMap.set(item.deviceId, {
              id: item.deviceId,
              externalId: item.serialNumber,
              name: item.deviceName,
              location: item.location,
              isCurrent: item.isCurrent,
            });
          } else {
            const existing = deviceMap.get(item.deviceId);
            if (existing && item.isCurrent) {
              existing.isCurrent = true;
            }
          }
        });
        const mappedHistorical = Array.from(deviceMap.values()).sort((a, b) => {
          const aCurrent = a.isCurrent !== false;
          const bCurrent = b.isCurrent !== false;
          if (aCurrent && !bCurrent) {
            return -1;
          }
          if (!aCurrent && bCurrent) {
            return 1;
          }
          return (a.name || a.externalId || '').localeCompare(b.name || b.externalId || '');
        });
        setHistoricalDevices(mappedHistorical);
      } else {
        const rawDevices = res && !res.error && Array.isArray(res.value) && res.value.length ? res.value : [];
        const sortedRaw = [...rawDevices].sort((a, b) => (a.name || a.externalId || '').localeCompare(b.name || b.externalId || ''));
        setHistoricalDevices(sortedRaw);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const handleDeleteClick = () => {
    if (devices.length > 0) {
      toast.error(t('toast.cannotDeleteStoreWithDevices', 'Não é possível apagar esta loja porque tem dispositivos associados.'));
      return;
    }
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteModalOpen(false);
    const res = await storeAPI.remove(storeId);
    if (res !== undefined && res !== null && !res.error) {
      toast.success(t('toast.storeDeleted', 'Loja eliminada'));
      navigate('/Gateway/Stores');
    } else {
      toast.error(res?.description || t('toast.storeDeleteError', 'Erro ao eliminar loja'));
    }
  };

  useEffect(() => { loadDevices(); }, [loadDevices]);

  if (storeLoading) return null;

  if (isForbidden) return <AccessDeniedState backPath={backPath} />;

  if (loading && !store) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Skeleton variant="text" width={100} height={20} animation="wave" />
          <Skeleton variant="text" width={140} height={20} animation="wave" />
        </Breadcrumbs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Skeleton variant="circular" width={36} height={36} animation="wave" />
          <Box>
            <Skeleton variant="text" width={180} height={28} animation="wave" />
            <Skeleton variant="text" width={120} height={18} animation="wave" />
          </Box>
        </Box>
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Skeleton variant="text" width={140} height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} variant="text" width="70%" height={22} animation="wave" />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (isNotFound || !store) return <NotFoundState backPath={backPath} />;

  const onlineCount = devices.filter(d => d.status === 'ONLINE').length;
  const offlineCount = devices.filter(d => d.status === 'OFFLINE').length;
  const warnCount = devices.filter(d => d.status === 'MAINTENANCE').length;

  const kpiItems = [
    { label: t('storeDetail.kpi.total'), value: devices.length, color: 'primary.main' },
    { label: t('storeDetail.kpi.online'), value: onlineCount, color: 'success.main' },
    { label: t('storeDetail.kpi.warning'), value: warnCount, color: 'warning.main' },
    { label: t('storeDetail.kpi.offline'), value: offlineCount, color: 'error.main' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {loading && <LinearProgress sx={{ position: 'absolute', top: 64, left: 0, width: '100%' }} />}

      {/* ── Breadcrumb ── */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="inherit"
          onClick={() => navigate(backPath)}
        >
          {breadcrumbLabel}
        </Link>
        <Typography
          variant="body2"
          color="text.primary"
        >{store.name}</Typography>
      </Breadcrumbs>

      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
        >
          <IconButton
            onClick={() => navigate(backPath)}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <StorefrontIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography
              variant="h2"
              sx={{ mb: 0.25 }}
            >{store.name}</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {store.externalId} · {client?.name ?? '—'}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          <Tooltip title={t('storeDetail.refreshTooltip')}>
            <IconButton onClick={loadDevices}><RefreshIcon /></IconButton>
          </Tooltip>

          {devices.length === 0 ? (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              sx={{ textTransform: 'none', height: 32, fontSize: '0.8125rem' }}
            >
              {t('storeDetail.deleteStore', 'Eliminar Loja')}
            </Button>
          ) : (
            <Tooltip title={t('storeDetail.deleteStoreTooltip', 'Não é possível apagar a loja porque tem dispositivos associados. Desassocie os dispositivos primeiro.')}>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  disabled
                  sx={{ textTransform: 'none', height: 32, fontSize: '0.8125rem', opacity: 0.4 }}
                >
                  {t('storeDetail.deleteStore', 'Eliminar Loja')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* ── Store info card ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography
            variant="h4"
            mb={1.5}
          >{t('storeDetail.infoCard.title')}</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 0.75,
            }}
          >
            <InfoRow
              label={t('storeDetail.infoCard.externalId')}
              value={store.externalId}
            />
            <InfoRow
              label={t('storeDetail.infoCard.city')}
              value={store.city}
            />
            <InfoRow
              label={t('storeDetail.infoCard.area')}
              value={store.area ? `${store.area} m²` : null}
            />
            <InfoRow
              label={t('storeDetail.infoCard.openedYear')}
              value={store.openedYear ?? null}
            />
            <InfoRow
              label={t('storeDetail.infoCard.client')}
              value={client?.name}
            />
          </Box>
        </CardContent>
      </Card>

      {/* ── KPI summary ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={2}
        mb={3}
      >
        {kpiItems.map(({ label, value, color }) => (
          <Card
            key={label}
            sx={{ flex: 1 }}
          >
            <CardContent sx={{ p: 2.5, textAlign: 'center', '&:last-child': { pb: 2.5 } }}>
              <Typography
                variant="display"
                color={color}
              >{value}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >{label}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* ── Counting stats ── */}
      <Box sx={{ mb: 3 }}>
        <StoreStatsSection
          clientId={store.clientId}
          storeId={storeId}
          devices={historicalDevices.length > 0 ? historicalDevices : devices}
        />
      </Box>

      {/* ── Devices table ── */}
      <Typography
        variant="h4"
        mb={1.5}
      >{t('storeDetail.devicesTable.title')}</Typography>
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        }}
      >
        <Table
          size="small"
          sx={{ '& .MuiTableCell-root': { fontSize: 13, borderColor: 'divider' } }}
        >
          <TableHead>
            <TableRow sx={TABLE_HEAD_ROW_SX}>
              <TableCell>{t('storeDetail.devicesTable.status')}</TableCell>
              <TableCell>{t('storeDetail.devicesTable.serial')}</TableCell>
              <TableCell>{t('storeDetail.devicesTable.nameLocation')}</TableCell>
              <TableCell>{t('storeDetail.devicesTable.model')}</TableCell>
              <TableCell>{t('storeDetail.devicesTable.firmware')}</TableCell>
              <TableCell>{t('storeDetail.devicesTable.lastContact')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map(device => (
              <TableRow
                key={device.id}
                hover
              >
                <TableCell>
                  <DeviceStatusIndicator
                    status={device.status}
                    lastHeartbeat={device.lastHeartbeat}
                    showLabel
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                  >{device.externalId}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{device.name || device.location || '—'}</Typography>
                  {device.location && device.name && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >{device.location}</Typography>
                  )}
                </TableCell>
                <TableCell>{device.model || '—'}</TableCell>
                <TableCell>{device.firmwareVersion || '—'}</TableCell>
                <TableCell>
                  {device.lastHeartbeat
                    ? fmtDateTime(device.lastHeartbeat)
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
            {!loading && !devices.length && (
              <EmptyTableRow
                colSpan={6}
                message={t('storeDetail.devicesTable.empty')}
              />
            )}
          </TableBody>
        </Table>
      </Box>

      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t('deleteStoreModal.title', 'Eliminar Loja')}
        message={t('deleteStoreModal.prompt', 'Tem a certeza que pretende eliminar esta loja? Esta ação é irreversível.')}
        confirmColor="error"
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default StoreDetailPage;
